import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "./api";
import { errorMessage } from "./api";
import { message } from "antd";
import type { Paginated } from "../api-models";

const RESERVED_KEYS = ["page", "pageSize", "search", "sort", "order"];

const DEBOUNCE_MS = 300;

export interface PaginatedListOptions {
  /**
   * Append pages instead of jumping between them, for endpoints that answer without a
   * total row count (the audit log — see PaginationMeta.hasMore in api-models). The page
   * number stops being URL state here: how far you have scrolled is not worth restoring,
   * and a deep link to ?page=7 with no rows above it would be a broken view.
   */
  loadMore?: boolean;
}

// URL-synced list state: page/pageSize/search/sort/order plus arbitrary filter params
// (architecture.md §9, §11). Reused by every ListPage instance.
export function usePaginatedList<T>(
  endpoint: string,
  extraParams?: Record<string, string | number | boolean | undefined>,
  options?: PaginatedListOptions,
) {
  const loadMoreMode = options?.loadMore ?? false;
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<T[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const page = Number(searchParams.get("page") ?? "1");
  const pageSize = Number(searchParams.get("pageSize") ?? "20");
  const search = searchParams.get("search") ?? "";
  const sort = searchParams.get("sort") ?? "";
  const order = searchParams.get("order") ?? "";

  const filters = useMemo(() => {
    const out: Record<string, string> = {};
    searchParams.forEach((v, k) => {
      if (!RESERVED_KEYS.includes(k)) out[k] = v;
    });
    return out;
  }, [searchParams]);

  // Every param change goes through here in one shot. setSearchParams is a navigate(),
  // not a queued state update — two calls in the same handler both read the pre-navigation
  // params and the last one wins, silently dropping the first.
  const setParams = useCallback(
    (patch: Record<string, string | number | null | undefined>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        let resetPage = false;
        for (const [key, value] of Object.entries(patch)) {
          if (value === undefined || value === null || value === "") next.delete(key);
          else next.set(key, String(value));
          if (key !== "page" && key !== "pageSize") resetPage = true;
        }
        // A new search/sort/filter invalidates whatever page you were on.
        if (resetPage) next.set("page", "1");
        return next;
      });
    },
    [setSearchParams],
  );

  const setParam = useCallback(
    (key: string, value?: string | number | null) => setParams({ [key]: value }),
    [setParams],
  );

  // Everything the user types or picks at — the search box and the page's own filters —
  // reaches the URL through one debounce, so a burst of keystrokes (or a change of mind on
  // a dropdown) costs one request instead of one each. `pending` is what the control shows
  // until its timer fires; the URL stays the source of truth otherwise, so the back button
  // and a pasted link both still work.
  const [pending, setPending] = useState<Record<string, string>>({});
  // One timer per key: changing a filter must not cancel a half-typed search.
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    const running = timers.current;
    return () => Object.values(running).forEach(clearTimeout);
  }, []);

  const setDebouncedParam = useCallback(
    (key: string, value?: string | number | null) => {
      const next = value === undefined || value === null ? "" : String(value);
      setPending((prev) => ({ ...prev, [key]: next }));
      clearTimeout(timers.current[key]);
      timers.current[key] = setTimeout(() => {
        delete timers.current[key];
        setParams({ [key]: next || null });
      }, DEBOUNCE_MS);
    },
    [setParams],
  );

  // The URL has moved — either our own debounced write landed, or the user went back — so
  // hand every param that is not still mid-edit back to it. Clearing the pending value in
  // the timer instead would blank the control for a frame: react-router's navigation and a
  // setState in the same tick do not land in the same render, so the control would lose the
  // typed text before the URL caught up and then have it reappear.
  useEffect(() => {
    setPending((prev) => {
      const stillEditing: Record<string, string> = {};
      for (const [key, value] of Object.entries(prev)) {
        if (timers.current[key]) stillEditing[key] = value;
      }
      return Object.keys(stillEditing).length === Object.keys(prev).length ? prev : stillEditing;
    });
  }, [search, filters]);

  // Pending keystrokes included, so a debounced control never stutters back to its old value.
  const paramValue = useCallback(
    (key: string) => (pending[key] ?? (key === "search" ? search : filters[key])) || undefined,
    [pending, search, filters],
  );

  // Everything the user can apply to narrow or reorder the list. Counted off the URL, not
  // `pending`, so the number always matches the rows on screen; a half-typed search bumps it
  // when its debounce lands. sort+order are one choice, so they count once.
  const activeFilterCount = useMemo(
    () => Object.keys(filters).length + (search ? 1 : 0) + (sort ? 1 : 0),
    [filters, search, sort],
  );

  // Drops search, sort/order, every page filter and the page number in one navigation.
  // pageSize is a view preference rather than a filter, so it survives. In-flight debounces
  // have to die with it or the timer would write a cleared control's old value straight back.
  const clearFilters = useCallback(() => {
    for (const key of Object.keys(timers.current)) {
      clearTimeout(timers.current[key]);
      delete timers.current[key];
    }
    setPending({});
    setSearchParams((prev) => {
      const next = new URLSearchParams();
      const size = prev.get("pageSize");
      if (size) next.set("pageSize", size);
      return next;
    });
  }, [setSearchParams]);

  const extraKey = JSON.stringify(extraParams ?? {});
  const filtersKey = JSON.stringify(filters);

  // Filters held in page-local state (not the URL) bypass setParams, so reset the page
  // here too — otherwise narrowing the list while on page 3 lands on an empty page.
  const prevExtraKey = useRef(extraKey);
  useEffect(() => {
    if (prevExtraKey.current === extraKey) return;
    prevExtraKey.current = extraKey;
    if (page !== 1) setParam("page", 1);
  }, [extraKey, page, setParam]);

  // How far "load more" has got, and which request is still the current one: a filter
  // changing mid-flight must not let the old query's rows land on top of the new list.
  const loadedPage = useRef(1);
  const requestSeq = useRef(0);

  const fetchPage = useCallback(
    async (target: number, append: boolean) => {
      const seq = ++requestSeq.current;
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const res = await api.get<Paginated<T>>(endpoint, {
          params: {
            page: target,
            pageSize,
            search: search || undefined,
            sort: sort || undefined,
            order: order || undefined,
            ...filters,
            ...extraParams,
          },
        });
        if (seq !== requestSeq.current) return;
        const rows = res.data.data ?? [];
        setData((prev) => (append ? [...prev, ...rows] : rows));
        setCount(res.data.count);
        // Uncounted endpoints say so; counted ones are measured against their total.
        setHasMore(res.data.meta?.hasMore ?? target * pageSize < res.data.count);
        loadedPage.current = target;
      } catch (err) {
        if (seq !== requestSeq.current) return;
        message.error(errorMessage(err));
      } finally {
        if (seq === requestSeq.current) {
          setLoadingMore(false);
          setLoading(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endpoint, pageSize, search, sort, order, filtersKey, extraKey],
  );

  // In load-more mode any change to the query starts the list again from the top; the
  // paged mode follows the page number in the URL as before.
  useEffect(() => {
    fetchPage(loadMoreMode ? 1 : page, false);
  }, [fetchPage, page, loadMoreMode]);

  const loadMore = useCallback(() => {
    if (loadingMore || loading || !hasMore) return;
    fetchPage(loadedPage.current + 1, true);
  }, [fetchPage, hasMore, loading, loadingMore]);

  const reload = useCallback(() => fetchPage(loadMoreMode ? 1 : page, false), [fetchPage, loadMoreMode, page]);

  return {
    data,
    count,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    page,
    pageSize,
    search,
    sort,
    order,
    filters,
    activeFilterCount,
    clearFilters,
    setParam,
    setParams,
    setDebouncedParam,
    paramValue,
    reload,
  };
}
